import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  ExternalLink,
  LoaderCircle,
  MapPin,
  Navigation,
  Route,
  Smartphone,
} from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';

const RIDER_APP_URL = (import.meta.env.VITE_SAWWARI_RIDER_APP_URL || '').trim();

function decodePolyline6(encoded) {
  const coordinates = [];
  let latitude = 0;
  let longitude = 0;
  let index = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    latitude += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    longitude += result & 1 ? ~(result >> 1) : result >> 1;

    coordinates.push([longitude / 1e6, latitude / 1e6]);
  }

  return coordinates;
}

function LocationField({
  id,
  label,
  icon,
  placeholder,
  value,
  selected,
  config,
  onValueChange,
  onSelect,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [searchState, setSearchState] = useState('idle');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const query = value.trim();
    if (!config || query.length < 3 || selected?.label === value) {
      setSuggestions([]);
      setSearchState('idle');
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearchState('loading');
      try {
        const params = new URLSearchParams({
          key: config.public_key,
          country: config.country_code,
          language: 'en',
          limit: '6',
          bbox: config.pakistan_bounds.join(','),
        });
        const response = await fetch(
          `${config.geocoding_url}/${encodeURIComponent(query)}.json?${params}`,
          { signal: controller.signal, headers: { Accept: 'application/json' } },
        );
        if (!response.ok) throw new Error(`Location search failed (${response.status})`);
        const payload = await response.json();
        const results = (payload.features || [])
          .filter((feature) => feature?.geometry?.type === 'Point')
          .map((feature) => ({
            id: feature.id || `${feature.geometry.coordinates[0]}-${feature.geometry.coordinates[1]}`,
            label: feature.place_name || feature.text,
            longitude: Number(feature.geometry.coordinates[0]),
            latitude: Number(feature.geometry.coordinates[1]),
          }))
          .filter((item) => item.label && Number.isFinite(item.latitude) && Number.isFinite(item.longitude));
        setSuggestions(results);
        setSearchState(results.length ? 'ready' : 'empty');
        setOpen(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setSuggestions([]);
          setSearchState('error');
          setOpen(true);
        }
      }
    }, 320);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [config, selected, value]);

  const Icon = icon;

  return (
    <label className="location-field" htmlFor={id}>
      <span>{label}</span>
      <div className={`input-shell ${selected ? 'input-shell--selected' : ''}`}>
        <Icon />
        <input
          id={id}
          type="search"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-suggestions`}
          required
          value={value}
          onChange={(event) => {
            onValueChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 140)}
          placeholder={placeholder}
        />
        {searchState === 'loading' && <LoaderCircle className="spin input-shell__loader" />}
        {selected && <CheckCircle2 className="input-shell__check" />}
      </div>
      {open && searchState !== 'idle' && (
        <div className="location-suggestions" id={`${id}-suggestions`} role="listbox">
          {searchState === 'error' && (
            <p><AlertCircle /> Location search is temporarily unavailable. Try again.</p>
          )}
          {searchState === 'empty' && <p>No matching Pakistan locations found.</p>}
          {suggestions.map((suggestion) => (
            <button
              type="button"
              role="option"
              aria-selected={selected?.id === suggestion.id}
              key={suggestion.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onSelect(suggestion);
                setOpen(false);
              }}
            >
              <MapPin />
              <span>{suggestion.label}</span>
            </button>
          ))}
          <small>Search powered by MapTiler</small>
        </div>
      )}
    </label>
  );
}

function buildAppHandoffUrl(pickup, destination) {
  if (!RIDER_APP_URL) return '';
  try {
    const url = new URL(RIDER_APP_URL);
    url.searchParams.set('pickup', pickup.label);
    url.searchParams.set('pickup_latitude', pickup.latitude);
    url.searchParams.set('pickup_longitude', pickup.longitude);
    url.searchParams.set('destination', destination.label);
    url.searchParams.set('destination_latitude', destination.latitude);
    url.searchParams.set('destination_longitude', destination.longitude);
    return url.toString();
  } catch {
    return RIDER_APP_URL;
  }
}

export function LaunchForm({ compact = false }) {
  const mapContainerRef = useRef(null);
  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [pickupLocation, setPickupLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [serviceState, setServiceState] = useState('loading');
  const [routeState, setRouteState] = useState('idle');
  const [routePreview, setRoutePreview] = useState(null);
  const [message, setMessage] = useState('');
  const [showAppHelp, setShowAppHelp] = useState(false);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    async function loadMapConfig() {
      setServiceState('loading');
      try {
        const response = await fetch('/api/v1/maps/public-config', {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Map configuration failed (${response.status})`);
        setMapConfig(await response.json());
        setServiceState('ready');
      } catch (error) {
        if (error.name !== 'AbortError') setServiceState('error');
      }
    }
    loadMapConfig();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (routeState !== 'ready' || !routePreview || !mapConfig || !mapContainerRef.current) {
      return undefined;
    }

    const coordinates = decodePolyline6(routePreview.route_shape);
    if (coordinates.length < 2) {
      setMapError(true);
      return undefined;
    }

    let map;
    let cancelled = false;

    async function initializeMap() {
      try {
        const { default: maplibregl } = await import('maplibre-gl');
        if (cancelled) return;
        const styleSeparator = mapConfig.style_url.includes('?') ? '&' : '?';
        map = new maplibregl.Map({
          container: mapContainerRef.current,
          style: `${mapConfig.style_url}${styleSeparator}key=${encodeURIComponent(mapConfig.public_key)}`,
          center: coordinates[0],
          zoom: 12,
          attributionControl: true,
        });
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
        new maplibregl.Marker({ color: '#5a52d6' })
          .setLngLat([pickupLocation.longitude, pickupLocation.latitude])
          .addTo(map);
        new maplibregl.Marker({ color: '#f36c5d' })
          .setLngLat([destinationLocation.longitude, destinationLocation.latitude])
          .addTo(map);

        map.on('load', () => {
          map.addSource('preview-route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates },
            },
          });
          map.addLayer({
            id: 'preview-route-line',
            type: 'line',
            source: 'preview-route',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#5a52d6',
              'line-width': 5,
              'line-opacity': 0.9,
            },
          });
          const bounds = coordinates.reduce(
            (current, coordinate) => current.extend(coordinate),
            new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
          );
          map.fitBounds(bounds, { padding: 54, maxZoom: 15, duration: 700 });
        });
        map.on('error', () => setMapError(true));
      } catch {
        if (!cancelled) setMapError(true);
      }
    }

    initializeMap();

    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [destinationLocation, mapConfig, pickupLocation, routePreview, routeState]);

  function resetPreview() {
    setRoutePreview(null);
    setRouteState('idle');
    setMessage('');
    setShowAppHelp(false);
    setMapError(false);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setShowAppHelp(false);
    if (serviceState !== 'ready') {
      setMessage('Live maps are temporarily unavailable. Please try again shortly.');
      return;
    }
    if (!pickupLocation || !destinationLocation) {
      setMessage('Choose both locations from the live suggestions before checking the route.');
      return;
    }

    setMessage('');
    setRouteState('loading');
    try {
      const response = await fetch('/api/v1/maps/preview-route', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin: {
            latitude: pickupLocation.latitude,
            longitude: pickupLocation.longitude,
          },
          destination: {
            latitude: destinationLocation.latitude,
            longitude: destinationLocation.longitude,
          },
          profile: 'auto',
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Route preview failed (${response.status})`);
      }
      setRoutePreview(await response.json());
      setRouteState('ready');
    } catch (error) {
      setRoutePreview(null);
      setRouteState('error');
      setMessage(error.message || 'A route could not be calculated for these locations.');
    }
  }

  const handoffUrl = pickupLocation && destinationLocation
    ? buildAppHandoffUrl(pickupLocation, destinationLocation)
    : '';

  return (
    <form className={`launch-form ${compact ? 'launch-form--compact' : ''}`} onSubmit={handleSubmit}>
      <div className="launch-form__head">
        <div>
          <span className="eyebrow">Live journey preview</span>
          <h2>Plan a city ride</h2>
        </div>
        <span className={`status-pill ${serviceState === 'error' ? 'status-pill--amber' : ''}`}>
          {serviceState === 'loading' ? <LoaderCircle className="spin" /> : serviceState === 'error' ? <AlertCircle /> : <i />}
          {serviceState === 'loading' ? 'Connecting' : serviceState === 'error' ? 'Map unavailable' : 'Live · Pakistan'}
        </span>
      </div>

      <LocationField
        id="ride-pickup"
        label="From"
        icon={MapPin}
        placeholder="Search pickup location"
        value={pickup}
        selected={pickupLocation}
        config={mapConfig}
        onValueChange={(value) => {
          setPickup(value);
          setPickupLocation(null);
          resetPreview();
        }}
        onSelect={(location) => {
          setPickup(location.label);
          setPickupLocation(location);
          resetPreview();
        }}
      />

      <LocationField
        id="ride-destination"
        label="To"
        icon={Navigation}
        placeholder="Search destination"
        value={destination}
        selected={destinationLocation}
        config={mapConfig}
        onValueChange={(value) => {
          setDestination(value);
          setDestinationLocation(null);
          resetPreview();
        }}
        onSelect={(location) => {
          setDestination(location.label);
          setDestinationLocation(location);
          resetPreview();
        }}
      />

      <button
        className="button button--primary launch-form__submit"
        type="submit"
        disabled={serviceState === 'loading' || routeState === 'loading'}
      >
        {routeState === 'loading' ? <><LoaderCircle className="spin" /> Calculating</> : <><Route /> Preview route</>}
      </button>

      {message && (
        <p className="launch-form__message" role="alert"><AlertCircle /> {message}</p>
      )}

      {routeState === 'ready' && routePreview && (
        <section className="journey-preview" aria-label="Journey preview">
          <div className="journey-preview__map-wrap">
            <div className="journey-preview__map" ref={mapContainerRef} />
            {mapError && (
              <div className="journey-preview__map-error">
                <AlertCircle /> The map could not be displayed, but the route summary remains available.
              </div>
            )}
          </div>
          <div className="journey-preview__summary">
            <span className="eyebrow">Live route estimate</span>
            <h3>{routePreview.distance_km.toFixed(1)} km · about {routePreview.estimated_minutes} min</h3>
            <div className="journey-preview__facts">
              <span><Route /> Road distance <strong>{routePreview.distance_km.toFixed(1)} km</strong></span>
              <span><Clock3 /> Estimated time <strong>{routePreview.estimated_minutes} min</strong></span>
            </div>
            <p>Distance and time are estimates. Traffic, road conditions and the final pickup pin can change the result.</p>
            {handoffUrl ? (
              <a className="button button--primary button--full" href={handoffUrl}>
                <Smartphone /> Continue in Sawwari app <ExternalLink />
              </a>
            ) : (
              <button
                className="button button--primary button--full"
                type="button"
                onClick={() => setShowAppHelp(true)}
              >
                <Smartphone /> Continue in Sawwari app
              </button>
            )}
            {showAppHelp && (
              <div className="app-handoff-note" role="status">
                <Smartphone />
                <p><strong>Open the Sawwari Rider app to continue.</strong> Set these locations in the app, sign in, review the live fare and confirm only when ready.</p>
              </div>
            )}
            <button className="journey-preview__reset" type="button" onClick={resetPreview}>
              Change journey
            </button>
          </div>
        </section>
      )}

      <p className="form-note">
        Suggestions are provided by MapTiler. Route distance and time are previews only; no ride, fare or payment is created on this website.
      </p>
    </form>
  );
}
